import { FC, useContext, useId, useRef, useState } from "react";
import { toastError, toastInfo } from "@app/exports/export-panel-body/toast";
import { buildMultiMapExportRequest } from "@app/projects/network-assessment/multi-map-export/utils/buildMultiMapExportRequest";
import {
  DEFAULT_OUTPUT_EMAIL_WHEN_READY,
  DEFAULT_OUTPUT_FILE_STRUCTURE,
  DEFAULT_OUTPUT_INCLUDE_PAGE_HEADER,
  DEFAULT_OUTPUT_REPORT_NAME,
  DEFAULT_OUTPUT_SHARED_USERS,
  MultiMapExportAction,
  MultiMapExportState,
} from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { REPORT_NAME_MAX_LENGTH, canCreateMaps, isReportNameValid } from "@app/projects/network-assessment/multi-map-export/utils/outputSelection";
import { ExportTypeEnum } from "@app/operations/exportTypeEnum";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import ExportService from "@app/exports/exportService";
import SharedUsers from "@app/reports/SharedUsers";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { MultiMapExportFileStructure, MultiMapExportMapStyleOption } from "@app/projects/network-assessment/multi-map-export/interfaces";

const TEST_ID = "multi-map-export__output";
const FILE_STRUCTURE_RADIO_NAME = "multi-map-export-output-file-structure";

const OUTPUT_FILE_STRUCTURE_OPTIONS: readonly MultiMapExportMapStyleOption<MultiMapExportFileStructure>[] = Object.freeze([
  { label: "Individual JPEGs, one per map", value: MultiMapExportFileStructure.IndividualJpegs },
  { label: "Individual PNGs, one per map", value: MultiMapExportFileStructure.IndividualPngs },
  { label: "Collated PDF, one map per page", value: MultiMapExportFileStructure.CollatedPdf },
]);

export const OutputSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { adequacyCombinations, project, projectRunConfiguration, selections, selectionsChanged } = useContext(MultiMapExportContext);
  const fileStructureLabelId = useId();
  const fileStructureHelperId = useId();
  const exportService = useRef(new ExportService()).current;
  const [isQueueing, setIsQueueing] = useState(false);

  const reportName = selections?.reportName ?? DEFAULT_OUTPUT_REPORT_NAME;
  const selectedFileStructure = selections?.selectedFileStructure ?? DEFAULT_OUTPUT_FILE_STRUCTURE;
  const includePageHeader = selections?.includePageHeader ?? DEFAULT_OUTPUT_INCLUDE_PAGE_HEADER;
  const emailWhenReady = selections?.emailWhenReady ?? DEFAULT_OUTPUT_EMAIL_WHEN_READY;
  const sharedUsers = [...(selections?.sharedUsers ?? DEFAULT_OUTPUT_SHARED_USERS)];

  const outputChanged = (value: MultiMapExportState) => selectionsChanged?.({
    type: MultiMapExportAction.OutputChanged,
    value,
  });

  const createMaps = async (): Promise<void> => {
    if (!project || !projectRunConfiguration) { return; }

    const trimmedReportName = reportName.trim();
    setIsQueueing(true);

    try {
      await exportService.postExportMultiMap(buildMultiMapExportRequest(
        {
          customProjectId: project.projectId,
          projectRunConfigurationId: projectRunConfiguration.projectRunConfigurationId,
          reportName: trimmedReportName,
        },
        selections,
      ));

      toastInfo(trimmedReportName);
    } catch {
      toastError(trimmedReportName);
    } finally {
      setIsQueueing(false);
    }
  };

  const renderFileStructureOption = (option: MultiMapExportMapStyleOption<MultiMapExportFileStructure>) => (
    <QESRadioButton
      checked={selectedFileStructure === option.value}
      key={option.value}
      label={option.label}
      name={FILE_STRUCTURE_RADIO_NAME}
      onChange={() => outputChanged({ selectedFileStructure: option.value })}
      radioContainerClassName="multi-map-export__output-option"
      testId={`${TEST_ID}-file-structure--${option.value}`}
      value={option.value}
    />
  );

  return (
    <section className="multi-map-export__output" data-testid={`${TEST_ID}-section`}>
      <div className="multi-map-export__output-header">
        <h2 className="multi-map-export__output-title">Output</h2>
      </div>
      <div className="multi-map-export__output-body">
        <div className="multi-map-export__field">
          <QESTextInput
            errorText="Enter a name for the export."
            isValid={isReportNameValid(reportName)}
            label="Report name"
            maxLength={REPORT_NAME_MAX_LENGTH}
            onChange={(event) => outputChanged({ reportName: event.target.value })}
            placeholder="Enter a name for the export"
            required={true}
            showCounter={false}
            testId={`${TEST_ID}-report-name`}
            value={reportName}
          />
        </div>
        <div className="multi-map-export__field" data-testid={`${TEST_ID}-shared-users`}>
          <SharedUsers
            containerClassnames="d-block"
            customProjectIds={project ? [project.projectId] : []}
            exportType={capitalizeFirstLetter(ExportTypeEnum.MultiMapExport)}
            sharedUsersFilter={sharedUsers}
            tooltipTitle="Users in this list have been filtered to only show users with access to this project and information in this report"
            updateSelectedSharedUsers={(newSharedUsers) => outputChanged({ sharedUsers: newSharedUsers })}
          />
        </div>
        <div className="multi-map-export__field">
          <span className="multi-map-export__field-label" id={fileStructureLabelId}>File Format</span>
          <p
            className="multi-map-export__field-helper-text multi-map-export__output-file-structure-helper"
            data-testid={`${TEST_ID}-file-structure-helper`}
            id={fileStructureHelperId}
          >
            All formats download as a single .zip file
          </p>
          <div aria-describedby={fileStructureHelperId} aria-labelledby={fileStructureLabelId} role="radiogroup">
            {OUTPUT_FILE_STRUCTURE_OPTIONS.map(renderFileStructureOption)}
          </div>
        </div>
        <div className="multi-map-export__field">
          <QESCheckbox
            checked={includePageHeader}
            containerClassName="multi-map-export__output-option"
            label="Page header on each map"
            onChange={(event) => outputChanged({ includePageHeader: event.target.checked })}
            testId={`${TEST_ID}-page-header`}
          />
          <QESCheckbox
            checked={emailWhenReady}
            containerClassName="multi-map-export__output-option"
            label="Email me when the export is ready"
            onChange={(event) => outputChanged({ emailWhenReady: event.target.checked })}
            testId={`${TEST_ID}-email-when-ready`}
          />
        </div>
        <p className="multi-map-export__output-message" data-testid={`${TEST_ID}-message`}>
          Large export. Report generation runs in the background and may take a while.
        </p>
        <div className="multi-map-export__output-actions">
          <QESButton
            className="multi-map-export__output-create-maps"
            disabled={isQueueing || !canCreateMaps(selections, adequacyCombinations)}
            onClick={createMaps}
            qesTestId={`${TEST_ID}-create-maps`}
            qesType="primary"
          >
            Create Maps
          </QESButton>
        </div>
      </div>
    </section>
  );
};

export default OutputSection;
